// Shared patch for CSSStyleSheet.insertRule so multiple concurrent popouts don't stomp each other.
// Emotion (used by react-select) injects styles via insertRule, bypassing DOM mutations entirely.
const insertRuleListeners = new Set<() => void>();
const originalInsertRule = CSSStyleSheet.prototype.insertRule;

export const addInsertRuleListener = (fn: () => void) => {
  if (insertRuleListeners.size === 0) {
    CSSStyleSheet.prototype.insertRule = function (rule: string, index?: number) {
      const result = originalInsertRule.call(this, rule, index);
      insertRuleListeners.forEach((listener) => listener());
      return result;
    };
  }
  insertRuleListeners.add(fn);
};

export const removeInsertRuleListener = (fn: () => void) => {
  insertRuleListeners.delete(fn);
  if (insertRuleListeners.size === 0) {
    CSSStyleSheet.prototype.insertRule = originalInsertRule;
  }
};

// Copy the app's styles into the new window
export const copyStyleSheets = (newHeadMountElement: HTMLDivElement) => {
  const stylesheets = Array.from(document.styleSheets);

  stylesheets.forEach((stylesheet) => {
    if (stylesheet.href) {
      const newStyleElement = document.createElement('link');
      newStyleElement.rel = 'stylesheet';
      newStyleElement.href = stylesheet.href;
      newHeadMountElement.appendChild(newStyleElement);
    } else {
      const css = stylesheet;
      if (css && css.cssRules && css.cssRules.length > 0) {
        const newStyleElement = document.createElement('style');
        Array.from(css.cssRules).forEach((rule) => {
          newStyleElement.appendChild(document.createTextNode(rule.cssText));
        });
        newHeadMountElement.appendChild(newStyleElement);
      }
    }
  });
};
/**
 * Add listener for dynamic style changes
 * This works by listening to any changes to the head element
 * If there are any changes it replaces all the styles in the floating window
 * at most every 100ms
 * @param onStyleChange throttled callback to trigger a style copy
 */
export const observeStyleSheetChanges = (onStyleChange: () => void) => {
  const targetNode = document.querySelector('head');

  if (!targetNode) {
    return null;
  }
  // Options for the observer (which mutations to observe)
  const config = { /*attributes: true,*/ childList: true /*, subtree: true */ };

  // Callback function to execute when mutations are observed
  const callback: MutationCallback = function (mutationList) {
    for (const mutation of mutationList) {
      if (mutation.type === 'childList') {
        // console.log("A child node has been added or removed.", mutation);
        //naive way up updating style sheets
        //the observer tells us what has been added and removed, but syncing the changes with the
        //floating window is complicated, so the easiest way is to just wait for modifications for finish
        //updating, then replace them all
        //we could probably just copy all style on every modification, by that might have a performance hit
        onStyleChange();
      } /*else if (mutation.type === "attributes") {
              //Do we need to handle attribute modifications?
              // console.log("The " + mutation.attributeName + " attribute was modified.");
            }*/
    }
  };

  // Create an observer instance linked to the callback function
  const observer = new MutationObserver(callback);

  // Start observing the target node for configured mutations
  observer.observe(targetNode, config);

  return observer;
};
